import React, { useState, useMemo } from "react";
import { Input, Button, Card, CardBody, Spinner, TableRow, TableCell } from "@nextui-org/react";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import ClientTablePagination from "../../../components/elements/ClientTablePagination"; // Path ke tabel pagination Anda    
import RootAdmin from "../../../components/layouts/RootAdmin";
import { convertLatLonToDecimal } from "../../../services/helpers";
import { postFetcher, postMultipartFetcher } from "../../../services/api";

const UploadAndValidateTable = () => {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processingRows, setProcessingRows] = useState([]);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const processExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets["Format Rekapitulasi"];
          const jsonData = XLSX.utils.sheet_to_json(sheet);

          // Process each row of the sheet
          const processedData = jsonData?.map((row, index) => {
            const latitude = row["Latitude (N/S)"] || "";
            const longitude = row["Longitude (E/W)"] || "";

            // Convert latitude and longitude to decimal degrees
            let decimalCoords = { x: null, y: null };
            try {
              decimalCoords = convertLatLonToDecimal(latitude, longitude);
            } catch (err) {
              toast.error(`Invalid coordinates on row ${index + 1}: ${err.message}`);
            }

            return {
              id: index + 1,
              provinceId: row["KODE_PROV"] || "",
              regencyId: row["KODE_KAB"] || "",
              districtId: row["KEC"] || "",
              villageId: row["DESA"] || "",
              jenisSampelId: row["KODE_JENIS"] || "",
              tahunPengambilan: row["THN_SMPL"] || "",
              hasilKadar: row["KADAR"]?.toString() || "",
              satuan: row["SATUAN"] || "",
              tingkatKadar: row["TK_MERKURI"] || "",
              konsentrasi: row["KONSNTRASI"] || "",
              sumberData: row["Sumber_Data"] || "",
              longitude: decimalCoords.x,
              latitude: decimalCoords.y,
              isValid: Boolean(
                row["KODE_JENIS"] &&
                row["THN_SMPL"] &&
                row["Latitude (N/S)"] &&
                row["Longitude (E/W)"] &&
                decimalCoords.x !== null &&
                decimalCoords.y !== null
              ),
            };
          });

          resolve(processedData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);

      reader.readAsArrayBuffer(file);
    });
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Silakan pilih file terlebih dahulu!");
      return;
    }

    setIsLoading(true);
    try {
      const processedData = await processExcelFile(file);
      setData(processedData);
      toast.success("File berhasil diproses!");
    } catch (error) {
      console.error("Gagal memproses file:", error);
      toast.error("Gagal memproses file.");
    } finally {
      setIsLoading(false);
      setFile(null);
    }
  };

  const handleBatchSubmit = async () => {
    const validData = data.filter((row) => row.isValid);
    const batchSize = 3; // Maximum rows to process simultaneously

    for (let i = 0; i < validData.length; i += batchSize) {
      const batch = validData.slice(i, i + batchSize);

      setProcessingRows(batch.map((row) => row.id));

      await Promise.allSettled(
        batch.map((row) =>
          submitRow(row).catch((err) => {
            console.error(`Error processing row ${row.id}:`, err);
          })
        )
      );

      setProcessingRows([]);
    }

    toast.success("Semua data telah diproses.");
  };

  const submitRow = async (row) => {
    try {
      const formData = new FormData();

      const dtoProperties = [
        "sumberData",
        "jenisSampelId",
        "hasilKadar",
        "satuan",
        "tingkatKadar",
        "konsentrasi",
        "tahunPengambilan",
        "provinceId",
        "regencyId",
        "latitude",
        "longitude",
      ];

      dtoProperties.forEach((key) => {
        if (row[key]) formData.append(key, row[key]);
      });

      await postMultipartFetcher("/api/mercury-monitoring", formData);
    } catch (error) {
      toast.error(`Data ID ${row.id} gagal diproses.`);
      throw error;
    }
  };

  const headers = [
    "No",
    "Province ID",
    "Regency ID",
    "District",
    "Village",
    "Jenis Sampel",
    "Tanggal Pengambilan",
    "Hasil Kadar",
    "Satuan",
    "Tingkat Kadar",
    "Konsentrasi",
    "Sumber Data",
    "Latitude",
    "Longitude",
    "Status",
  ];

  const content = (row) => (
    <TableRow
      key={row.id}
      style={{
        backgroundColor: processingRows.includes(row.id) ? "yellow" : row.isValid ? "white" : "lightgray",
      }}
    >
      <TableCell>{row.id}</TableCell>
      <TableCell>{row.provinceId || "-"}</TableCell>
      <TableCell>{row.regencyId || "-"}</TableCell>
      <TableCell>{row.districtId || "-"}</TableCell>
      <TableCell>{row.villageId || "-"}</TableCell>
      <TableCell>{row.jenisSampelId || "-"}</TableCell>
      <TableCell>{row.tahunPengambilan || "-"}</TableCell>
      <TableCell>{row.hasilKadar || "-"}</TableCell>
      <TableCell>{row.satuan || "-"}</TableCell>
      <TableCell>{row.tingkatKadar || "-"}</TableCell>
      <TableCell>{row.konsentrasi || "-"}</TableCell>
      <TableCell>{row.sumberData || "-"}</TableCell>
      <TableCell>{row.latitude || "-"}</TableCell>
      <TableCell>{row.longitude || "-"}</TableCell>
      <TableCell>{processingRows.includes(row.id) ? "Processing..." : row.isValid ? "Valid" : "Invalid"}</TableCell>
    </TableRow>
  );

  return (
    <RootAdmin>
      <Card>
        <CardBody>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <Input
              type="file"
              label="Unggah File"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              isDisabled={isLoading}
              variant="bordered"
              size="lg"
              helperText="Hanya file Excel yang diperbolehkan"
            />

            <Button
              color="primary"
              isDisabled={!file || isLoading}
              onPress={handleUpload}
              isLoading={isLoading}
              loadingText="Memproses..."
            >
              Upload dan Proses
            </Button>
          </div>
        </CardBody>
      </Card>
      <ClientTablePagination data={data} isLoading={isLoading} header={headers} content={content} />
      <div className="flex justify-end mt-4">
        <Button color="success" isDisabled={isLoading || data.length === 0} onPress={handleBatchSubmit}>
          Submit Semua Data
        </Button>
      </div>
    </RootAdmin>
  );
};

export default UploadAndValidateTable;
